import { useEffect, useRef, useState, type FormEvent } from "react";
import { gameArt } from "@unfaithful/client";
import { isValidCastleCode, normaliseCastleCode, type CodeLookup, type GameInfo } from "@unfaithful/shared";
import "./hub.css";

/**
 * THE PLAYPOP HOME SCREEN (phone). What people see when they type playpop.tv:
 * one big box for the code on the TV, and the games they could host.
 * A valid code hands over to that game's own join screen.
 */
/** Smart TVs and TV sticks: their browsers say so in the user agent. */
function isTvBrowser(): boolean {
  return /SmartTV|SMART-TV|Tizen|Web0S|WebOS|NetCast|BRAVIA|HbbTV|GoogleTV|Android TV|AFT[A-Z]|CrKey|Roku|Viera|AppleTV|PhilipsTV|VIDAA|Hisense|Opera TV|OMI\/|NETTV|Titan|\bTV\b/i.test(navigator.userAgent);
}

/**
 * A screen to host from (a TV, a laptop, a tablet on its side) rather than a
 * phone. TV browsers often report a small width (960 px), so this goes by
 * shape and pointer rather than size alone: phones are touch and upright.
 */
function isBigScreen(): boolean {
  const phoneLike = window.matchMedia("(hover: none) and (pointer: coarse) and (max-width: 900px)").matches;
  return !phoneLike && window.matchMedia("(min-width: 700px)").matches;
}

export function Hub({ onCode }: { onCode: (game: string, code: string) => void }) {
  // A TV that opens playpop.tv wants the host screen, not the phone's join box.
  const [bigScreen] = useState(isBigScreen);
  useEffect(() => {
    if (isTvBrowser()) location.replace("/tv/");
  }, []);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [games, setGames] = useState<GameInfo[] | null>(null);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    document.title = "PlayPop";
    fetch("/api/games")
      .then((r) => (r.ok ? r.json() : { games: [] }))
      .then((b: { games: GameInfo[] }) => setGames(b.games))
      .catch(() => setGames([]));
  }, []);

  const ready = isValidCastleCode(code);

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!ready) return setError("Codes are 4 letters. They're on the TV.");
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/castles/${code}`, { cache: "no-store" });
      if (res.status === 429) throw new Error("Slow down a moment, then try again.");
      const found = (await res.json()) as CodeLookup;
      if (!found.found || !found.game) throw new Error(`No game is using ${code}. Check the TV.`);
      if (!found.joinable) throw new Error("That game has already started or is full.");
      onCode(found.game, code);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't check that code. Try again.");
      setBusy(false);
    }
  };

  return (
    <div className="pp">
      <main className="pp-screen">
        <header className="pp-top">
          <Wordmark />
          <p className="pp-lead">Party games on your TV. Your phone is the controller.</p>
        </header>

        {bigScreen && (
          <a className="pp-host-here" href="/tv/">
            <span className="pp-host-here-title">On the TV or laptop?</span>
            <span className="pp-host-here-line">Host a game on this screen. Everyone joins on their phone.</span>
            <span className="pp-host-here-go">Host here</span>
          </a>
        )}

        <form className="pp-join" onSubmit={submit} noValidate>
          <label className="pp-label" htmlFor="pp-code">
            Joining a game?
          </label>
          <input
            id="pp-code"
            ref={input}
            className="pp-code"
            value={code}
            onChange={(e) => {
              const next = normaliseCastleCode(e.target.value).slice(0, 4);
              setCode(next);
              setError(null);
            }}
            inputMode="text"
            autoCapitalize="characters"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="go"
            maxLength={8}
            placeholder="CODE"
            aria-describedby="pp-code-help"
          />
          <p id="pp-code-help" className="pp-help">
            Type the 4 letters on the TV
          </p>
          {error && (
            <p className="pp-error" role="alert">
              {error}
            </p>
          )}
          <button className="pp-go" type="submit" disabled={!ready || busy}>
            {busy ? "Checking…" : "Join"}
          </button>
        </form>

        <section className="pp-host" aria-labelledby="pp-host-title">
          <h2 id="pp-host-title" className="pp-h2">
            Hosting tonight?
          </h2>
          <p className="pp-help">
            Open <strong>playpop.tv/tv</strong> in the browser on your TV or laptop, pick a game, then everyone joins with
            the code.
          </p>
          <ul className="pp-games">
            {games === null && <li className="pp-card pp-card-loading" aria-hidden />}
            {games?.map((g) => <GameCard key={g.id} game={g} />)}
            <li className="pp-card pp-card-more">
              <span className="pp-card-name">More games soon</span>
              <span className="pp-card-pitch">New games arrive here as they're ready.</span>
            </li>
          </ul>
        </section>

        <footer className="pp-foot">
          <a href="mailto:hello@playpop.tv">hello@playpop.tv</a>
        </footer>
      </main>
    </div>
  );
}

function GameCard({ game }: { game: GameInfo }) {
  const art = gameArt(game.id, `phone-${game.id}`);
  return (
    <li className={`pp-card ${art ? "has-art" : ""}`} data-game={game.id} style={{ "--accent": game.accent } as React.CSSProperties}>
      {art && <div className="pp-card-art" dangerouslySetInnerHTML={{ __html: art }} />}
      <div className="pp-card-body">
        <span className={`pp-card-name pp-name-${game.id}`}>{game.name}</span>
        <span className="pp-card-pitch">{game.pitch}</span>
        <span className="pp-card-meta">
          {game.players}, {game.minutes}
          {game.status === "coming-soon" && ". Coming soon"}
        </span>
      </div>
    </li>
  );
}

export function Wordmark({ size = "md" }: { size?: "md" | "lg" }) {
  return (
    <h1 className={`pp-wordmark pp-wordmark-${size}`} aria-label="PlayPop">
      <span aria-hidden>Play</span>
      <span className="pp-pop" aria-hidden>
        Pop
      </span>
      <span className="pp-dot" aria-hidden>
        .tv
      </span>
    </h1>
  );
}
